import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../atoms/Button'
import { Input } from '../../../atoms/Input'
import { Icon } from '../../../atoms/Icon'

const StyledModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  min-width: 500px;
`

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`

const StyledTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`

const StyledTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

const StyledTab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: transparent;
  border: none;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-weight: ${({ theme, $active }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.normal};
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const StyledInputGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

const StyledLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

const StyledDropZone = styled.div<{ $isDragging: boolean }>`
  border: 2px dashed
    ${({ theme, $isDragging }) =>
      $isDragging ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.xlarge};
  text-align: center;
  background: ${({ theme, $isDragging }) =>
    $isDragging
      ? theme.colors.background.secondary
      : theme.colors.background.primary};
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`

const StyledDropZoneText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing.small} 0;
`

const StyledButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme }) => theme.spacing.large};
`

const StyledFileInput = styled.input`
  display: none;
`

const StyledPreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: ${({ theme }) => theme.spacing.medium};
`

interface ImageUploaderProps {
  onImageInsert: (url: string) => void
  onClose: () => void
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageInsert,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'url' | 'upload'>('url')
  const [imageUrl, setImageUrl] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleUrlSubmit = () => {
    if (imageUrl) {
      onImageInsert(imageUrl)
    }
  }

  const handleFileSelect = (file: File) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        setUploadedImage(result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleUploadSubmit = () => {
    if (uploadedImage) {
      onImageInsert(uploadedImage)
    }
  }

  return (
    <>
      <StyledOverlay onClick={onClose} />
      <StyledModal>
        <StyledTitle>Insert Image</StyledTitle>

        <StyledTabs>
          <StyledTab
            $active={activeTab === 'url'}
            onClick={() => setActiveTab('url')}
          >
            From URL
          </StyledTab>
          <StyledTab
            $active={activeTab === 'upload'}
            onClick={() => setActiveTab('upload')}
          >
            Upload File
          </StyledTab>
        </StyledTabs>

        {activeTab === 'url' ? (
          <>
            <StyledInputGroup>
              <StyledLabel>Image URL</StyledLabel>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                autoFocus
              />
            </StyledInputGroup>

            {imageUrl && <StyledPreview src={imageUrl} alt="Preview" />}

            <StyledButtonGroup>
              <Button variant="secondary" size="small" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleUrlSubmit}
                disabled={!imageUrl}
              >
                Insert Image
              </Button>
            </StyledButtonGroup>
          </>
        ) : (
          <>
            <StyledDropZone
              $isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="image" size="large" />
              <StyledDropZoneText>
                Drop an image here or click to upload
              </StyledDropZoneText>
              <StyledDropZoneText>
                Supports: JPG, PNG, GIF, WebP
              </StyledDropZoneText>
            </StyledDropZone>

            <StyledFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
            />

            {uploadedImage && (
              <StyledPreview src={uploadedImage} alt="Preview" />
            )}

            <StyledButtonGroup>
              <Button variant="secondary" size="small" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleUploadSubmit}
                disabled={!uploadedImage}
              >
                Insert Image
              </Button>
            </StyledButtonGroup>
          </>
        )}
      </StyledModal>
    </>
  )
}
